import React, { useEffect, useRef, useState } from "react";

const ADMIN_LINKS = [
  { label: "Projects", path: "/interior-editor/projects" },
  { label: "Library", path: "/interior-editor/library" },
  { label: "Assets", path: "/interior-editor/assets" },
  { label: "Catalog", path: "/interior-editor/catalog" },
  { label: "Materials", path: "/interior-editor/materials" },
  { label: "Categories", path: "/interior-editor/categories" },
  { label: "Parameters Registry", path: "/interior-editor/parameters" },
];

function TopButton({
  children,
  onClick,
  disabled,
  danger,
  primary,
  subtle,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "px-3 py-1.5 rounded-md text-sm border whitespace-nowrap transition disabled:opacity-40 disabled:cursor-not-allowed",
        primary
          ? "bg-green-600 text-white border-green-600 hover:bg-green-700"
          : danger
          ? "bg-white text-red-600 border-red-200 hover:bg-red-50"
          : subtle
          ? "bg-transparent text-slate-600 border-transparent hover:bg-slate-100"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function ManageMenu({
  open,
  onClose,
  onNavigate,
  onDeleteProject,
  onOpenParameterManager,
}) {
  if (!open) return null;

  return (
    <div className="absolute right-0 top-10 z-50 w-72 rounded-xl border bg-white shadow-xl p-2">
      <div className="px-2 py-1.5">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          Manage editor
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          onOpenParameterManager?.();
          onClose?.();
        }}
        className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-50"
      >
        Project parameters
      </button>

      <div className="my-2 border-t" />

      <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        Admin pages
      </div>

      {ADMIN_LINKS.map((item) => (
        <button
          key={item.path}
          type="button"
          onClick={() => {
            onNavigate?.(item.path);
            onClose?.();
          }}
          className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-50"
        >
          {item.label}
        </button>
      ))}

      <div className="my-2 border-t" />

      <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-red-400">
        Danger zone
      </div>

      <button
        type="button"
        onClick={() => {
          onDeleteProject?.();
          onClose?.();
        }}
        className="w-full text-left px-3 py-2 rounded-md text-sm text-red-600 hover:bg-red-50"
      >
        Delete project
      </button>
    </div>
  );
}

export default function EditorTopBar({
  projectName,
  projectDescription,
  onProjectNameChange,
  onProjectDescriptionChange,

  saving,
  canUndo,
  canRedo,

  onSave,
  onUndo,
  onRedo,
  onDeleteProject,
  onOpenParameterManager,
  onNavigate,
}) {
  const [manageOpen, setManageOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!manageOpen) return;

      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setManageOpen(false);
      }
    }

    window.addEventListener("pointerdown", handlePointerDown);
    return () => window.removeEventListener("pointerdown", handlePointerDown);
  }, [manageOpen]);

  return (
    <div className="shrink-0 border-b bg-white">
      <div className="min-h-14 px-3 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center text-xs font-bold">
              MO
            </div>

            <div className="hidden sm:block">
              <div className="font-semibold text-sm text-slate-800 leading-tight">
                Interior Editor
              </div>
              <div className="text-[11px] text-slate-400 leading-tight">
                Designer workspace
              </div>
            </div>
          </div>

          <div className="w-px h-8 bg-slate-200 shrink-0" />

          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="min-w-[180px] max-w-[320px] flex-1">
              <input
                value={projectName}
                onChange={(e) => onProjectNameChange?.(e.target.value)}
                className="w-full px-3 py-1.5 border rounded-md text-sm bg-white font-medium text-slate-800"
                placeholder="Project name"
              />
            </div>

            <div className="hidden lg:block min-w-[220px] max-w-[420px] flex-1">
              <input
                value={projectDescription}
                onChange={(e) =>
                  onProjectDescriptionChange?.(e.target.value)
                }
                className="w-full px-3 py-1.5 border rounded-md text-sm bg-white text-slate-600"
                placeholder="Description"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <TopButton onClick={onUndo} disabled={!canUndo} subtle>
            Undo
          </TopButton>

          <TopButton onClick={onRedo} disabled={!canRedo} subtle>
            Redo
          </TopButton>

          <TopButton onClick={onOpenParameterManager}>
            Parameters
          </TopButton>

          <div ref={menuRef} className="relative">
            <TopButton onClick={() => setManageOpen((prev) => !prev)}>
              Manage
            </TopButton>

            <ManageMenu
              open={manageOpen}
              onClose={() => setManageOpen(false)}
              onNavigate={onNavigate}
              onDeleteProject={onDeleteProject}
              onOpenParameterManager={onOpenParameterManager}
            />
          </div>

          <TopButton primary onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </TopButton>
        </div>
      </div>
    </div>
  );
}